import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Clock, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/tasks`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);

      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData);
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        setCurrentUser(userData);

        // Fetch all users for task assignment (both admin and employees can assign tasks)
        const usersRes = await authFetch(`${BACKEND_URL}/api/users/team`);
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData);
        }
      }
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Task created successfully');
        setDialogOpen(false);
        setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
        fetchData();
      } else {
        toast.error('Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Task updated');
        fetchData();
      } else {
        toast.error('Failed to update task');
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await authFetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Task deleted');
        fetchData();
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      in_progress: 'bg-blue-50 text-blue-700 border-blue-100',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-100'
    };
    return styles[status] || styles.pending;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-50 text-red-700 border-red-100',
      medium: 'bg-amber-50 text-amber-700 border-amber-100',
      low: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return styles[priority] || styles.medium;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  const groupedTasks = {
    pending: tasks.filter(t => t.status === 'pending'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    completed: tasks.filter(t => t.status === 'completed')
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Task Management</h1>
            <p className="text-slate-600 mt-1">Manage and track your tasks</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-task-button" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </Button>
            </DialogTrigger>
              <DialogContent data-testid="create-task-dialog">
                <DialogHeader>
                  <DialogTitle>Create New Task</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTask} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Task Title *</Label>
                    <Input
                      id="title"
                      data-testid="task-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="Enter task title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="task-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter task description"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                        <SelectTrigger data-testid="task-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        type="date"
                        data-testid="task-duedate-input"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="assigned_to">Assign To</Label>
                    <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                      <SelectTrigger data-testid="task-assignto-select">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.user_id} value={user.user_id}>
                            {user.name} - {user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-task-button">Create Task</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
        </div>

        {/* Kanban Board */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Column */}
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-amber-100 p-2 rounded-md mr-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Pending</h3>
              <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                {groupedTasks.pending.length}
              </span>
            </div>
            <div className="space-y-3" data-testid="pending-tasks-column">
              {groupedTasks.pending.map((task) => (
                <Card key={task.task_id} className="card-hover" data-testid={`task-${task.task_id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-900">{task.title}</h4>
                      {(currentUser?.role === 'admin' || task.assigned_by === currentUser?.user_id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-task-${task.task_id}`}
                          onClick={() => handleDeleteTask(task.task_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                      {task.due_date && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                          {task.due_date}
                        </span>
                      )}
                    </div>
                    <Select value={task.status} onValueChange={(value) => handleUpdateStatus(task.task_id, value)}>
                      <SelectTrigger data-testid={`update-status-${task.task_id}`} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
              {groupedTasks.pending.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No pending tasks
                </div>
              )}
            </div>
          </div>

          {/* In Progress Column */}
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-2 rounded-md mr-3">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">In Progress</h3>
              <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                {groupedTasks.in_progress.length}
              </span>
            </div>
            <div className="space-y-3" data-testid="inprogress-tasks-column">
              {groupedTasks.in_progress.map((task) => (
                <Card key={task.task_id} className="card-hover" data-testid={`task-${task.task_id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-900">{task.title}</h4>
                      {(currentUser?.role === 'admin' || task.assigned_by === currentUser?.user_id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-task-${task.task_id}`}
                          onClick={() => handleDeleteTask(task.task_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                      {task.due_date && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                          {task.due_date}
                        </span>
                      )}
                    </div>
                    <Select value={task.status} onValueChange={(value) => handleUpdateStatus(task.task_id, value)}>
                      <SelectTrigger data-testid={`update-status-${task.task_id}`} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
              {groupedTasks.in_progress.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No tasks in progress
                </div>
              )}
            </div>
          </div>

          {/* Completed Column */}
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-emerald-100 p-2 rounded-md mr-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Completed</h3>
              <span className="ml-2 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                {groupedTasks.completed.length}
              </span>
            </div>
            <div className="space-y-3" data-testid="completed-tasks-column">
              {groupedTasks.completed.map((task) => (
                <Card key={task.task_id} className="card-hover opacity-75" data-testid={`task-${task.task_id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-900">{task.title}</h4>
                      {(currentUser?.role === 'admin' || task.assigned_by === currentUser?.user_id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-task-${task.task_id}`}
                          onClick={() => handleDeleteTask(task.task_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                      {task.due_date && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                          {task.due_date}
                        </span>
                      )}
                    </div>
                    <Select value={task.status} onValueChange={(value) => handleUpdateStatus(task.task_id, value)}>
                      <SelectTrigger data-testid={`update-status-${task.task_id}`} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
              {groupedTasks.completed.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No completed tasks
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}